import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppExpander from "@toothfairy/shared-ui/AppExpander";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import trainingAPI from "../../../API/Training/trainingAPI";
import LoadingPlaceholder from "../../../Components/LoadingPlaceholder";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import FunctionLeaf from "./FunctionLeaf";
import ReferenceLeaf from "./ReferenceLeaf";
import ChatScriptLeaf from "./ChatScriptLeaf";
import NerViewComponent from "../../MrcDocuments/Components/NerViewComponent";
import { AppText } from "@toothfairy/shared-ui";
const ReferencesView = ({ selectedSources, S3downloadUrlRequest }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const { t } = useTranslation();
  const [references, setReferences] = React.useState([]);
  const [internetReferences, setInternetReferences] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [s3FnDetails, setS3FnDetails] = React.useState(null);
  const [isLoadingFnDetails, setIsLoadingFnDetails] = React.useState(false);

  // Fetch fnDetails from S3 on demand when fnDetailsS3 reference is present
  useEffect(() => {
    const fetchFnDetailsFromS3 = async () => {
      if (
        selectedSources?.fnDetailsS3?.s3_key &&
        S3downloadUrlRequest &&
        (!selectedSources?.fn || selectedSources.fn.length === 0)
      ) {
        setIsLoadingFnDetails(true);
        try {
          const result = await S3downloadUrlRequest({
            filename: selectedSources.fnDetailsS3.s3_key,
            context: "fndetails",
            workspaceid:
              selectedSources?.workspaceid || getActiveWorkspace()?.id,
          });
          if (result?.url) {
            const response = await fetch(result.url);
            const data = await response.json();
            setS3FnDetails(data || []);
          }
        } catch (e) {
          console.warn("[ReferencesView] Failed to fetch fnDetails from S3:", e);
          setS3FnDetails([]);
        } finally {
          setIsLoadingFnDetails(false);
        }
      } else {
        setS3FnDetails(null);
      }
    };
    fetchFnDetailsFromS3();
  }, [selectedSources?.fnDetailsS3?.s3_key]);

  const fetchSources = async () => {
    try {
      setIsLoading(true);
      setReferences([]);
      const promises = selectedSources?.docs?.map((source) => {
        return trainingAPI.getEmebddingById(
          source.id,
          getActiveWorkspace()?.id
        );
      });
      const results = await Promise.all(promises);
      setReferences(
        results?.map((result) => {
          return result?.data?.data;
        })
      );
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (selectedSources?.docs?.filter((u) => u?.type != "web")?.length > 0) {
      fetchSources();
    } else {
      setReferences([]);
    }
    setInternetReferences(
      selectedSources?.webPages?.length > 0
        ? selectedSources?.webPages?.filter(
            (u) => u?.tf_type == "news" || u?.tf_type == "search"
          )
        : []
    );
  }, [JSON.stringify(selectedSources)]);
  // Use S3-fetched fnDetails when available, otherwise fall back to inline fn array
  const fnList = s3FnDetails || selectedSources?.fn || [];
  const dataSources = fnList.filter(
    (u) => u?.type == "api" || u?.type == "graphql"
  );
  const dbSources = fnList.filter((u) => u?.type == "db");
  const codeSources = fnList.filter((u) => u?.type == "code");
  const chatFunctions = fnList.filter(
    (u) =>
      u?.type == "suggestion" ||
      u?.type == "nextQuestion" ||
      u?.type == "script"
  );
  const htmlFunctions = fnList.filter((u) => u?.type == "html");
  const mcpServers = fnList.filter((u) => u?.type == "mcp");
  const otherSources = fnList.filter(
    (u) =>
      u?.type != "api" &&
      u?.type != "graphql" &&
      u?.type != "db" &&
      u?.type != "code" &&
      u?.type != "suggestion" &&
      u?.type != "nextQuestion" &&
      u?.type != "script" &&
      u?.type != "html" &&
      u?.type != "mcp"
  );
  return isLoading || isLoadingFnDetails ? (
    <LoadingPlaceholder visible caption={t("chats.ui.loading")} />
  ) : (
    <View>
      {
        <AppExpander
          expanderStyle={{
            padding: 0,
          }}
          style={{ minWidth: 250 }}
          isExpandedOnLoad={false}
          title={`${t("chats.ui.documentation")} (${references?.length || 0})`}
          icon={AppAssets.icons.notes}
          isEnabled={references?.length > 0}
        >
          {references?.map((reference, i) => {
            return (
              <View
                style={{
                  paddingLeft: 0,
                }}
              >
                <ReferenceLeaf
                  key={`${i}-${Date.now()?.toString()}`}
                  {...reference}
                  selectedSources={selectedSources?.docs}
                />
                <NerViewComponent
                  showHover={false}
                  chunk_id={i}
                  ners={reference?.ners}
                />
              </View>
            );
          })}
        </AppExpander>
      }
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.webpages")} (${internetReferences?.length || 0})`}
        icon={AppAssets.icons.internet}
        isEnabled={internetReferences?.length > 0}
      >
        {internetReferences?.map((webpage, i) => {
          return (
            <AppText
              renderMarkdown={false}
              isClickable
              leftIcon={
                webpage?.tf_type == "news"
                  ? AppAssets.icons.news
                  : AppAssets?.icons?.web
              }
              key={`${webpage["link"]}${i}`}
              onPress={() => {
                window.open(webpage["link"], "_blank");
              }}
              color={currentTheme?.theme?.primary}
              iconSize={16}
              wrapperStyle={{
                alignItems: "flex-start",
                marginBottom: 5,
                paddingLeft: 15,
              }}
              style={{
                marginTop: 0,
                paddingLeft: 15,
              }}
            >
              {webpage["title"]}
            </AppText>
          );
        })}
      </AppExpander>
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.apiOperations")} (${dataSources?.length || 0})`}
        icon={AppAssets.icons.api}
        isEnabled={dataSources?.length > 0}
      >
        {dataSources?.map((fn, i) => {
          return (
            <FunctionLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.mcpServers")} (${mcpServers?.length || 0})`}
        icon={AppAssets.icons.mcp}
        isEnabled={mcpServers?.length > 0}
      >
        {mcpServers?.map((fn, i) => {
          return (
            <FunctionLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.pythonCode")} (${codeSources?.length || 0})`}
        icon={AppAssets.icons.code}
        isEnabled={codeSources?.length > 0}
      >
        {codeSources?.map((fn, i) => {
          return (
            <FunctionLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>

      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.dbProcedures")} (${dbSources?.length || 0})`}
        icon={AppAssets.icons.data}
        isEnabled={dbSources?.length > 0}
      >
        {dbSources?.map((fn, i) => {
          return (
            <FunctionLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>

      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.chatScripts")} (${chatFunctions?.length || 0})`}
        icon={AppAssets.icons.quantumChat}
        isEnabled={chatFunctions?.length > 0}
      >
        {chatFunctions?.map((fn, i) => {
          return (
            <ChatScriptLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.htmlIFrames")} (${htmlFunctions?.length || 0})`}
        icon={AppAssets.icons.html}
        isEnabled={htmlFunctions?.length > 0}
      >
        {htmlFunctions?.map((fn, i) => {
          return (
            <ChatScriptLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>
      <AppExpander
        expanderStyle={{
          padding: 0,
        }}
        style={{ minWidth: 250 }}
        isExpandedOnLoad={false}
        title={`${t("chats.ui.otherTools")} (${otherSources?.length || 0})`}
        icon={AppAssets.icons.tools}
        isEnabled={otherSources?.length > 0}
      >
        {otherSources?.map((fn, i) => {
          return (
            <FunctionLeaf key={`${i}-${Date.now()?.toString()}`} fn={fn} />
          );
        })}
      </AppExpander>
    </View>
  );
};

export default ReferencesView;
